import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';

import { State } from '../../redux/mainReducer';
import { IDocument } from '../../Interfaces/IDocument';
import { getFetch } from '../../hooks/useFetch';
import {
  base64ToBlob,
  getMimeTypeString,
} from '../../LegalTransaction/DocumentDirectory/documentDownload';

interface IProps {
  idDocument: number | undefined;
  fileName: string;
  height?: number;
  /** Wird beim Klick auf ein Bild mit dessen Objekt-URL gerufen. Ohne diese
   *  Angabe ist die Vorschau nicht anklickbar. */
  onZoom?: (objectUrl: string) => void;
  /**
   * Inhalt einer noch nicht gespeicherten Datei (Base64), wie er beim Hochladen
   * im Browser entsteht. Ist er gesetzt, wird direkt daraus angezeigt — ohne
   * Nachladen, denn ein `idDocument` gibt es dafür noch nicht.
   */
  pendingData?: string | null;
  /** MIME-Typ der noch nicht gespeicherten Datei. */
  pendingIdMIMEType?: number | null;
}

/**
 * Vorschau einer einzelnen Datei. Der Inhalt einer Version eines Dokumenttyps
 * steht bewusst nicht in den Listen des Mandanten und wird deshalb hier einzeln
 * nachgeladen — so lassen sich mehrere Vorschauen nebeneinander betreiben,
 * etwa beim Versionsvergleich.
 *
 * Frisch hochgeladene Dateien haben noch kein `idDocument`, ihre Bytes liegen
 * aber schon im Browser. Über `pendingData` lassen sich auch sie ansehen —
 * wichtig, um vor dem Speichern zu prüfen, ob die richtige Datei gewählt wurde.
 *
 * Der Inhalt wird als Objekt-URL (Blob) eingebunden, nicht als `data:`-URL.
 * Grund: eine `data:`-URL im `src` eines Frames ist eine Navigation, und
 * Chromium verwirft Navigations-URLs ab 2 MB Laenge — ohne Fehlermeldung, der
 * Rahmen bleibt einfach leer. Genau das passierte bei den Ausweisfotos aus dem
 * Ident-Service: 4-5 MB je Bild ergeben rund 5,5 MB Base64. Kleine PDFs blieben
 * darunter, deshalb fiel es lange nicht auf. Ein Objekt-URL ist ein kurzer
 * Verweis und kennt diese Grenze nicht. Das gilt für hochgeladene Dateien
 * genauso, deshalb nimmt `pendingData` denselben Weg.
 *
 * Bilder bekommen ein `img` statt eines Frames: der Frame wuerde das Foto in
 * der Browser-Bildansicht oeffnen, mit eigenem Hintergrund und Zoomverhalten.
 */
export const DocumentInlinePreview: React.FC<IProps> = ({
  idDocument,
  fileName,
  height = 520,
  onZoom,
  pendingData,
  pendingIdMIMEType,
}) => {
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const [documentObject, setDocumentObject] = useState<IDocument | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const hasPendingData =
    pendingData !== null && pendingData !== undefined && pendingData !== '';

  const mimeTypeString = useMemo(() => {
    if (hasPendingData) {
      return getMimeTypeString(pendingIdMIMEType ?? 1, mimeTypeArray);
    }

    return documentObject
      ? getMimeTypeString(documentObject.idMIMEType, mimeTypeArray)
      : '';
  }, [hasPendingData, pendingIdMIMEType, documentObject, mimeTypeArray]);

  useEffect(() => {
    setDocumentObject(null);
    setHasFailed(false);

    // Eine noch nicht gespeicherte Datei liegt bereits vor; ihre ID ist
    // entweder gar nicht oder nur temporaer (negativ) vorhanden, ein Nachladen
    // ginge also zwangslaeufig ins Leere.
    if (hasPendingData || idDocument === undefined || idDocument < 0) {
      return;
    }

    let isActive = true;

    getFetch('/document/', idDocument, (loaded: IDocument) => {
      if (!isActive) {
        return;
      }

      if (loaded === null || loaded === undefined) {
        setHasFailed(true);

        return;
      }

      setDocumentObject(loaded);
    });

    return () => {
      isActive = false;
    };
  }, [idDocument, hasPendingData]);

  const sourceData = hasPendingData ? pendingData : documentObject?.Data;

  useEffect(() => {
    if (!sourceData || !mimeTypeString) {
      return;
    }

    let createdUrl: string | null = null;

    try {
      createdUrl = URL.createObjectURL(base64ToBlob(sourceData, mimeTypeString));
      setObjectUrl(createdUrl);
    } catch {
      // Beschaedigtes Base64 laesst `atob` werfen. Ohne diesen Zweig risse die
      // ganze Seite mit — eine unlesbare Datei darf nur ihre eigene Vorschau
      // kosten.
      setHasFailed(true);
    }

    return () => {
      setObjectUrl(null);

      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [sourceData, mimeTypeString]);

  if (!hasPendingData) {
    if (idDocument === undefined) {
      return <Alert severity='info'>Keine Datei ausgewählt.</Alert>;
    }

    if (idDocument < 0) {
      return (
        <Alert severity='info'>
          Die Datei „{fileName}“ steht erst nach dem Speichern in der Vorschau
          zur Verfügung.
        </Alert>
      );
    }
  }

  if (hasFailed) {
    return (
      <Alert severity='warning'>
        Die Datei „{fileName}“ konnte nicht geladen werden.
      </Alert>
    );
  }

  if (objectUrl === null) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        height={height}
      >
        <CircularProgress />
      </Box>
    );
  }

  const displayName = documentObject?.FileName ?? fileName;

  if (mimeTypeString.startsWith('image/')) {
    return (
      <Box
        component='img'
        src={objectUrl}
        alt={displayName}
        onClick={onZoom ? () => onZoom(objectUrl) : undefined}
        sx={{
          display: 'block',
          width: '100%',
          height,
          objectFit: 'contain',
          borderRadius: 1,
          backgroundColor: 'action.hover',
          cursor: onZoom ? 'zoom-in' : 'default',
        }}
      />
    );
  }

  return (
    <iframe
      title={displayName}
      name={displayName}
      src={objectUrl}
      width='100%'
      height={height}
      style={{ border: 0 }}
    />
  );
};
